'use client';

import * as React from 'react';
import { useEffect, useCallback, useMemo, startTransition } from 'react';
import { X, ArrowLeft, CaretRight } from '@phosphor-icons/react';
import {
  CartIcon,
  CheckIcon,
  ClockIcon,
  DropIcon,
  DumbbellIcon,
  FireIcon,
  LeafIcon,
  LinkIcon,
  PlusIcon,
  SmartphoneIcon,
  ThermometerIcon,
} from '@/components/icons';
import { cn } from '@/lib/utils';
import { formatPrice, formatWeight as formatWeightValue } from '@/lib/utils';
import { generateSlug } from '@/lib/seo/slug';
import { siteConfig } from '@/config/site';
import { Button, Spinner } from '@/components/ui';
import { ThemedImage } from '@/components/ui/themed-image';
import { QuantitySelector } from '@/components/ui/inputs/quantity-selector';
import {
  useProductPopupStore,
  selectIsPopupOpen,
  selectCurrentProduct,
  selectIsViewingNested,
  selectOriginalProduct,
  selectSetIngredients,
  selectIsLoadingIngredients,
  selectQuantity,
} from '@/stores/product-popup-store';
import { useCartStore } from '@/stores/cart-store';
import { useProducts } from '@/hooks/use-products';
import { parseAllergens } from '@/lib/allergens';
import { AllergenIcon } from '@/components/features/products/allergen-icon';
import { getSetIngredients, type ProductWithCategories } from '@/lib/api/products';

// ========== CONSTANTS ==========

// ID категорий сетов
const SET_CATEGORY_IDS = [2, 9, 16];

// ID категории соусов для рекомендаций
const SAUCE_CATEGORY_ID = 7;





// ── Description helper: strip info notices & render them as styled cards at the bottom ──

const INFO_PATTERNS = [
  /Ролл не является горячим[^.]*\./g,
  /Запекание[^.]*температура\./g,
  /Рекомендуется употребить[^.]*\./g,
  /Хранить при[^.]*\./g,
];

function stripInfoNotices(text: string): string {
  let clean = text;
  for (const pattern of INFO_PATTERNS) {
    pattern.lastIndex = 0;
    clean = clean.replace(pattern, '');
  }
  return clean.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Аллергены блюда (вопрос 30, вариант А).
 * Показываем только то, что реально размечено в админке: пустое поле — это
 * «не размечено», и молчаливое «аллергенов нет» здесь было бы обещанием,
 * которого никто не давал. Ссылка на общую страницу остаётся всегда.
 */
function ProductAllergens({ allergens, className }: { allergens?: string | null; className?: string }) {
  const items = parseAllergens(allergens);
  if (items.length === 0) return null;

  return (
    <div className={cn('rounded-card bg-[var(--bg-elevated)] p-4', className)}>
      <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-2">
        Аллергены
      </p>
      <ul className="flex flex-wrap gap-2">
        {items.map((item) => (
          <li
            key={item.code}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-pill bg-[var(--surface-secondary)] t-caption text-[var(--text-primary)]"
          >
            <AllergenIcon code={item.code} size={16} className="shrink-0" />
            {item.label}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-[var(--text-tertiary)] leading-relaxed">
        Готовим на общей кухне — возможны следы других аллергенов.{' '}
        <a href="/allergens" className="text-[var(--primary)] hover:underline">
          Подробнее
        </a>
      </p>
    </div>
  );
}

const HEAT_PATTERN = /Ролл не является горячим|Запекание[^.]*температура/;
const STORAGE_PATTERN = /Рекомендуется употребить|Хранить при/;

function ProductInfoNotices({ description }: { description?: string | null }) {
  if (!description) return null;

  const hasHeat = HEAT_PATTERN.test(description);
  const hasStorage = STORAGE_PATTERN.test(description);

  if (!hasHeat && !hasStorage) return null;

  return (
    <div className="space-y-2 mt-3 pt-3 border-t border-[var(--border-default)]">
      {hasHeat && (
        <div className="flex items-start gap-2.5 rounded-card bg-[var(--surface)] p-3">
          <ThermometerIcon className="w-4 h-4 text-[var(--primary)] flex-shrink-0 mt-0.5" weight="duotone" />
          <p className="text-xs text-[var(--text-tertiary)] leading-relaxed">
            Ролл не является горячим. Запекание — способ приготовления верхней «шапочки», а&nbsp;не&nbsp;температура
          </p>
        </div>
      )}
      {hasStorage && (
        <div className="flex items-start gap-2.5 rounded-card bg-[var(--surface)] p-3">
          <ClockIcon className="w-4 h-4 text-[var(--primary)] flex-shrink-0 mt-0.5" weight="duotone" />
          <p className="text-xs text-[var(--text-tertiary)] leading-relaxed">
            Рекомендуется употребить в&nbsp;течение 3‑х часов (от&nbsp;даты и&nbsp;времени на&nbsp;чеке). Хранить при&nbsp;+2°C до&nbsp;+6°C не&nbsp;более 16&nbsp;часов
          </p>
        </div>
      )}
    </div>
  );
}

// ── Share icons ── extracted from project's own design SVGs, unified viewBox 44×44 ──

const VkIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 44 44" fill="currentColor" className={className}>
    <path d="M23.5184 34C12.4165 34 5.87297 25.4288 5.07397 11.0386C5.01196 9.92187 5.91727 9 7.03567 9H8.85185C9.94341 9 10.8276 9.87755 10.8948 10.967C11.3688 18.653 13.8091 23.0071 16.3603 25.0531C17.4023 25.8887 18.6976 24.9786 18.6976 23.643V11C18.6976 9.89543 19.593 9 20.6976 9H22.1769C23.2814 9 24.1769 9.89543 24.1769 11V16.6152C24.1769 17.8196 25.2583 18.7268 26.2924 18.1093C28.5694 16.7495 30.6779 13.8261 31.807 10.5138C32.1053 9.63852 32.8984 9 33.8232 9H35.4043C36.6267 9 37.5694 10.0917 37.239 11.2687C36.2933 14.6379 34.5046 17.3956 32.7114 19.3223C31.6958 20.4135 31.7909 22.3686 32.9021 23.3623C34.863 25.116 36.9641 27.803 38.2959 31.5809C38.7241 32.7956 37.7682 34 36.4803 34H34.4966C33.5945 34 32.8164 33.3905 32.4997 32.5458C31.2869 29.3112 29.1266 26.7442 26.3486 25.6219C25.2389 25.1736 24.1769 26.1172 24.1769 27.3141V33.3416C24.1769 33.7052 23.8821 34 23.5184 34Z"/>
  </svg>
);

const WaIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 44 44" fill="currentColor" className={className}>
    <path d="M22 8C14.268 8 8 14.268 8 22c0 2.469.645 4.786 1.775 6.795L8.1 35.9l7.33-1.623A13.94 13.94 0 0022 36c7.732 0 14-6.268 14-14S29.732 8 22 8zm0 25.2c-2.2 0-4.35-.6-6.22-1.74l-.44-.27-4.6 1.02 1.06-4.48-.29-.46A11.14 11.14 0 0110.8 22c0-6.18 5.02-11.2 11.2-11.2S33.2 15.82 33.2 22 28.18 33.2 22 33.2zm6.14-8.38c-.34-.17-2-.99-2.31-1.1-.31-.11-.54-.17-.76.17-.23.34-.88 1.1-1.08 1.33-.2.23-.39.25-.73.08-.34-.17-1.43-.53-2.73-1.68-1.01-.9-1.69-2.01-1.89-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.39.5-.59.17-.2.23-.34.34-.56.11-.23.06-.42-.03-.59-.08-.17-.76-1.83-1.04-2.51-.27-.66-.55-.57-.76-.58h-.65c-.23 0-.59.08-.9.42-.31.34-1.18 1.15-1.18 2.81 0 1.66 1.21 3.26 1.38 3.49.17.23 2.38 3.63 5.76 5.09.81.35 1.44.56 1.93.71.81.26 1.55.22 2.13.13.65-.1 2-.82 2.28-1.61.28-.79.28-1.47.2-1.61-.09-.14-.31-.23-.65-.39z"/>
  </svg>
);

// Share section component
const ShareSection = React.memo(({ productName, productId, productPrice, compact = false }: { productName: string; productId: number; productPrice: number; compact?: boolean }) => {
  const [copied, setCopied] = React.useState(false);

  const slug = generateSlug(productName, productId);
  const shareUrl = `${siteConfig.url}/product/${slug}`;
  const shareText = `${productName} — ${productPrice} ₽ | ${siteConfig.name}`;

  const handleCopyLink = React.useCallback(() => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareUrl]);

  const handleNativeShare = React.useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: productName,
          text: shareText,
          url: shareUrl,
        });
      } catch {
        // User cancelled or share failed — fall back to copy
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  }, [productName, shareText, shareUrl, handleCopyLink]);

  const vkShareUrl = `https://vk.com/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}&image=${encodeURIComponent('')}`;
  const waShareUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;

  return (
    <div className={cn('flex items-center justify-between py-3.5', compact ? 'px-4' : '')}>
      <span className="text-xs text-[var(--text-tertiary)]">Поделиться</span>
      <div className="flex items-center gap-2">
        <a
          href={vkShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Поделиться ВКонтакте"
          className="flex items-center justify-center w-9 h-9 rounded-pill bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[#0077FF] hover:bg-[var(--surface-hover)] transition-colors"
        >
          <VkIcon className="w-5 h-5" />
        </a>
        <a
          href={waShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Поделиться в WhatsApp"
          className="flex items-center justify-center w-9 h-9 rounded-pill bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[#25D366] hover:bg-[var(--surface-hover)] transition-colors"
        >
          <WaIcon className="w-5 h-5" />
        </a>
        <button
          onClick={handleNativeShare}
          aria-label={copied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-pill transition-colors',
            copied
              ? 'bg-emerald-100 text-emerald-600'
              : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--surface-hover)]'
          )}
        >
          {copied ? (
            <CheckIcon weight="bold" className="w-5 h-5" />
          ) : (
            <LinkIcon weight="bold" className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
});
ShareSection.displayName = 'ShareSection';

// ========== MOBILE APP PROMO ==========

const GooglePlayIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.196 12l2.502-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
  </svg>
);

const AppleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const MobileAppPromo = React.memo(({ compact = false }: { compact?: boolean }) => {
  return (
    <div className={cn(compact ? 'px-4 py-3' : 'p-4')}>
      <div className="relative overflow-hidden rounded-card bg-gradient-to-br from-[var(--primary)] to-[#5B28D1] p-4">
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-pill bg-white/10" />
        <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-pill bg-white/5" />

        <div className="relative z-10">
          {/* Header row */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-pill bg-white/15 flex-shrink-0">
              <SmartphoneIcon weight="duotone" className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-tight">
                Скачайте приложение
              </p>
              <p className="text-[11px] text-white/60 mt-0.5">
                Бонусы и эксклюзивные предложения
              </p>
            </div>
          </div>

          {/* Store buttons */}
          <div className="flex items-center gap-2">
            <a
              href="https://play.google.com/store/apps/details?id=com.steepdesign.aziaapp"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-card px-3 py-2 bg-white hover:bg-white/90 active:scale-[0.97] transition-all duration-200"
            >
              <GooglePlayIcon className="w-4 h-4 text-[#2D1B69] flex-shrink-0" />
              <span className="text-xs font-semibold text-[#2D1B69]">Google Play</span>
            </a>

            <a
              href="https://apps.apple.com/ru/app/азиаточка-доставка-еды/id6761508832"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-card px-3 py-2 bg-white hover:bg-white/90 active:scale-[0.97] transition-all duration-200"
            >
              <AppleIcon className="w-4 h-4 text-[#2D1B69] flex-shrink-0" />
              <span className="text-xs font-semibold text-[#2D1B69]">App Store</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});
MobileAppPromo.displayName = 'MobileAppPromo';

// ========== HELPER COMPONENTS ==========

// Nutrition Tile Component
interface NutritionTileProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  compact?: boolean;
}

const NutritionTile = ({ icon, value, label, compact = false }: NutritionTileProps) => {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-card bg-[var(--primary-subtle)]',
        compact ? 'p-2.5' : 'p-3'
      )}
    >
      <div className="text-[var(--primary)]">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className={cn(
          'font-semibold text-[var(--text-primary)] truncate',
          compact ? 'text-xs' : 'text-sm'
        )}>
          {value}
        </p>
        <p className={cn(
          'text-[var(--text-secondary)]',
          compact ? 'text-[10px]' : 'text-xs'
        )}>
          {label}
        </p>
      </div>
    </div>
  );
};

// Set Ingredient Card
interface SetIngredientCardProps {
  ingredient: ProductWithCategories;
  onPress: () => void;
  index: number;
  compact?: boolean;
}

const SetIngredientCard = ({ ingredient, onPress, index, compact = false }: SetIngredientCardProps) => {
  return (
    <button
      onClick={onPress}
      style={{ animationDelay: `${index * 50}ms` }}
      className={cn(
        'flex items-center gap-3 rounded-card w-full text-left',
        'bg-[var(--surface)] border border-[var(--border-default)]',
        'hover:border-[var(--primary)]/30 hover:bg-[var(--surface-hover)]',
        'transition-all duration-200',
        'group',
        'animate-fade-up',
        compact ? 'p-1.5' : 'p-2'
      )}
    >
      {/* Image */}
      <div className={cn(
        'relative rounded-card overflow-hidden flex-shrink-0 bg-[var(--bg-tertiary)]',
        compact ? 'w-14 h-14' : 'w-20 h-20'
      )}>
        <ThemedImage
          src={ingredient.image_url}
          alt={ingredient.name}
          fill
          className="object-cover"
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 py-1">
        <h4 className={cn(
          'font-semibold text-[var(--text-primary)] line-clamp-2 mb-0.5',
          compact ? 'text-xs' : 'text-sm'
        )}>
          {ingredient.name}
        </h4>
        {ingredient.weight && (
          <p className={cn(
            'text-[var(--text-secondary)]',
            compact ? 'text-[10px]' : 'text-xs'
          )}>
            {formatWeightValue(ingredient.weight)}
          </p>
        )}
      </div>

      {/* Arrow */}
      <CaretRight
        className={cn(
          'text-[var(--text-tertiary)] group-hover:text-[var(--primary)] transition-colors flex-shrink-0',
          compact ? 'w-4 h-4' : 'w-5 h-5'
        )}
        weight="bold"
      />
    </button>
  );
};

// ========== MAIN COMPONENT ==========

export function ProductPopup() {
  // Store
  const isOpen = useProductPopupStore(selectIsPopupOpen);
  const product = useProductPopupStore(selectCurrentProduct);
  const isViewingNested = useProductPopupStore(selectIsViewingNested);
  const originalProduct = useProductPopupStore(selectOriginalProduct);
  const setIngredients = useProductPopupStore(selectSetIngredients);
  const isLoadingIngredients = useProductPopupStore(selectIsLoadingIngredients);
  const quantity = useProductPopupStore(selectQuantity);

  // Scroll container refs
  const mobileScrollRef = React.useRef<HTMLDivElement>(null);
  const desktopScrollRef = React.useRef<HTMLDivElement>(null);

  // Храним последний product для анимации закрытия
  const lastProductRef = React.useRef(product);
  if (product) {
    lastProductRef.current = product;
  }
  const displayProduct = product || lastProductRef.current;

  const closePopup = useProductPopupStore((state) => state.closePopup);
  const pushProduct = useProductPopupStore((state) => state.pushProduct);
  const popProduct = useProductPopupStore((state) => state.popProduct);
  const setLoadingIngredients = useProductPopupStore((state) => state.setLoadingIngredients);
  const setSetIngredients = useProductPopupStore((state) => state.setSetIngredients);
  const setQuantity = useProductPopupStore((state) => state.setQuantity);

  // Cart Store
  const addItem = useCartStore((state) => state.addItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const cartItems = useCartStore((state) => state.items);

  // Products for sauces
  const { data: allProducts } = useProducts();
  // Показываем ВСЕ соусы категории, а не первые четыре. Раньше блок обрезался
  // slice(0, 4), и в эти четыре попадали только бесплатные — платные соусы на
  // сайте не предлагались нигде, хотя в приложении список полный.
  // Сортировка по цене: бесплатные остаются сверху, платные идут следом.
  const sauceProducts = useMemo(() => {
    if (!allProducts) return [];
    return allProducts
      .filter((prod) => prod.categories.some((cat) => cat.id === SAUCE_CATEGORY_ID))
      .sort((a, b) => a.price - b.price);
  }, [allProducts]);



  // Quick add sauce to cart
  const handleAddSauce = useCallback((sauce: ProductWithCategories) => {
    startTransition(() => {
      addItem(sauce, 1);
    });
  }, [addItem]);

  // Change sauce quantity (0 = remove)
  const handleSauceQuantityChange = useCallback((sauceId: number, newQty: number) => {
    startTransition(() => {
      updateQuantity(sauceId, newQty);
    });
  }, [updateQuantity]);

  // Check if product is a set
  const isSetProduct = useMemo(() => {
    if (!product?.categories) return false;
    return product.categories.some((cat) => SET_CATEGORY_IDS.includes(cat.id));
  }, [product]);

  // Load set ingredients
  useEffect(() => {
    const loadIngredients = async () => {
      if (isSetProduct && product && !isViewingNested) {
        setLoadingIngredients(true);
        try {
          const ingredients = await getSetIngredients(product.id);
          setSetIngredients(ingredients);
        } catch (error) {
          console.error('Error loading set ingredients:', error);
          setSetIngredients([]);
        } finally {
          setLoadingIngredients(false);
        }
      }
    };

    loadIngredients();
  }, [isSetProduct, product, isViewingNested, setLoadingIngredients, setSetIngredients]);

  // Сброс скролла при открытии попапа и при смене продукта (навигация по сету)
  useEffect(() => {
    if (product) {
      mobileScrollRef.current?.scrollTo(0, 0);
      desktopScrollRef.current?.scrollTo(0, 0);
    }
  }, [product]);

  // Lock body scroll — без position:fixed чтобы не сбрасывать позицию скролла
  useEffect(() => {
    if (isOpen) {
      // Запоминаем ширину скроллбара чтобы не было сдвига контента
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Handle close/back с startTransition
  const handleClose = useCallback(() => {
    startTransition(() => {
      if (isViewingNested) {
        popProduct();
      } else {
        closePopup();
      }
    });
  }, [isViewingNested, popProduct, closePopup]);

  // Handle add to cart с startTransition
  const handleAddToCart = useCallback(() => {
    if (!product) return;

    startTransition(() => {
      addItem(product, quantity);

      if (isViewingNested) {
        popProduct();
      } else {
        closePopup();
      }
    });
  }, [product, quantity, addItem, isViewingNested, popProduct, closePopup]);

  // Handle ingredient click с startTransition
  const handleIngredientClick = useCallback(
    (ingredient: ProductWithCategories) => {
      startTransition(() => {
        pushProduct(ingredient);
      });
    },
    [pushProduct]
  );

  // Вес всегда с единицей: раньше карточка печатала голое число рядом с ценой,
  // а страница товара дописывала «г» сама — один и тот же вес показывался
  // по-разному (вопрос 31, вариант А: в базе число, единицу дописывает интерфейс).
  const formatWeight = (weight?: number | string | null): string =>
    formatWeightValue(weight) ?? '—';

  // Handle quantity change
  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      setQuantity(newQuantity);
    },
    [setQuantity]
  );

  // Мемоизированные вычисления
  const existingCartItem = useMemo(() => 
    cartItems.find((item) => item.id === displayProduct?.id), 
    [cartItems, displayProduct?.id]
  );
  const existingQuantity = useMemo(() => existingCartItem?.quantity || 0, [existingCartItem]);
  const totalPrice = useMemo(() => (displayProduct?.price || 0) * quantity, [displayProduct?.price, quantity]);
  const isAvailable = useMemo(() => displayProduct?.status === 'available', [displayProduct?.status]);

  // Mobile nutrition popover toggle
  const [showNutrition, setShowNutrition] = React.useState(false);

  // Reset nutrition popover when product changes
  useEffect(() => {
    setShowNutrition(false);
  }, [displayProduct?.id]);
  
  // Мемоизированное форматирование цен
  const formattedProductPrice = useMemo(() => formatPrice(displayProduct?.price || 0), [displayProduct?.price]);
  const formattedTotalPrice = useMemo(() => formatPrice(totalPrice), [totalPrice]);

  if (!displayProduct) return null;

  // Алиас для рендера — используем displayProduct чтобы контент оставался во время анимации закрытия
  const p = displayProduct;

  // Рендерим всегда когда есть displayProduct, но управляем видимостью через CSS transitions
  const showPopup = isOpen;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] transition-[visibility] duration-0',
        showPopup ? 'visible' : 'invisible delay-400'
      )}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/60 backdrop-blur-md',
          'transition-opacity duration-300 ease-out',
          showPopup ? 'opacity-100' : 'opacity-0'
        )}
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="absolute inset-0 flex items-end md:items-start md:justify-center pointer-events-none p-0 md:pt-[5vh] md:px-3">
        {/* Modal */}
        <div
          className={cn(
            'pointer-events-auto w-full relative',
            'overflow-hidden',
            // Mobile: fullscreen
            'h-[100dvh]',
            // Desktop: centered, max-width, rounded top only
            'md:max-w-7xl md:h-[96vh] md:max-h-none md:rounded-t-screen md:rounded-b-none',
            // Background & Shadow
            'bg-[var(--surface-secondary)]',
            // Модалка — второе и последнее место, где системе разрешён объём
            // (ответ 35 = 2). Поэтому deep, а не float: уплощение сюда не идёт.
            'shadow-deep',
            // CSS transition — выезд снизу
            'transition-transform duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]',
            showPopup ? 'translate-y-0' : 'translate-y-full'
          )}
        >
          {/* Close Button — Desktop only (mobile has its own) */}
          <button
            onClick={handleClose}
            className={cn(
              'absolute z-20 items-center justify-center rounded-pill',
              'transition-all duration-200 active:scale-95',
              // Hidden on mobile, visible on desktop
              'hidden md:flex',
              'md:top-4 md:right-4 md:w-11 md:h-11',
              'md:bg-black/30 md:backdrop-blur-md md:text-white',
              'md:hover:bg-black/50 md:hover:text-white'
            )}
          >
            <X weight="bold" className="w-6 h-6" />
          </button>

          {/* ============ MOBILE LAYOUT (fullscreen) ============ */}
          <div className="md:hidden flex flex-col h-full">
            {/* Top bar: back/close — always on top */}
            <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))]">
              {isViewingNested ? (
                <button
                  onClick={popProduct}
                  className="flex items-center justify-center w-10 h-10 rounded-pill bg-black/30 backdrop-blur-md text-white hover:bg-black/50 transition-colors active:scale-95"
                >
                  <ArrowLeft weight="bold" className="w-5 h-5" />
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={handleClose}
                className="flex items-center justify-center w-10 h-10 rounded-pill bg-black/30 backdrop-blur-md text-white hover:bg-black/50 transition-colors active:scale-95"
              >
                <X weight="bold" className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Content — scrollable */}
            <div ref={mobileScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden overlay-scrollbar">

              {/* Sticky image — stays in place while content scrolls over it */}
              <div className="sticky top-0 -z-10 w-full h-[65vh] overflow-hidden bg-[var(--bg-tertiary)]">
                <ThemedImage
                  key={p.id}
                  src={p.image_url}
                  alt={p.name}
                  fill
                  className="object-cover"
                />
              </div>

              {/* Content that scrolls over the image */}
              <div
                className="relative z-10 -mt-48"
                // Локальная палитра мобильного попапа: контент лежит не на
                // фоне сайта, а на сиреневом #ECD4F4, поэтому цвета заданы
                // здесь, а не берутся из темы.
                //
                // --text-tertiary был #8D80A1 — тот самый цвет, который
                // фундамент увёл на #74678F по ответу 37 = А. Здесь он
                // возвращал старое значение и гасил правку на самом
                // посещаемом экране. Но и #74678F тут не проходит: на
                // сиреневом фоне он даёт 3,75 при норме 4,5 (на белом было
                // 5,15 — фон темнее, запаса нет). Взят тот же оттенок,
                // затемнённый до AA на САМОЙ ТЁМНОЙ из пяти поверхностей
                // попапа: 4,56 на #ECD4F4 и 4,8-5,9 на остальных.
                // По светлоте он остаётся на 6 единиц L* светлее вторичного —
                // иерархия «вторичный/третичный» не схлопывается.
                style={{
                  '--text-primary': '#2C1360',
                  '--text-secondary': '#5B4A73',
                  '--text-tertiary': '#665B7E',
                  '--primary': '#7B42FF',
                  '--primary-hover': '#6B35E8',
                } as React.CSSProperties}
              >
                {/* Gradient + name overlay area */}
                <div className="px-5 pb-4 pt-40 bg-gradient-to-t from-[#ECD4F4] via-[#ECD4F4]/70 via-50% to-transparent">
                  {isViewingNested && (
                    <p className="text-[var(--text-tertiary)] text-xs mb-1 flex items-center gap-1">
                      <CaretRight weight="bold" className="w-3 h-3" />
                      {originalProduct?.name}
                    </p>
                  )}
                  <h1 className="text-2xl font-bold text-[var(--text-primary)] leading-tight">{p.name}</h1>
                </div>

                {/* Main content */}
                <div
                  className={cn(
                    "px-5 pt-3 space-y-5 bg-[#ECD4F4] pb-6"
                  )}
                  style={{
                    '--bg-elevated': '#FBF5FE',
                    '--bg-tertiary': '#F3E8F8',
                    '--surface': '#F3EEFF',
                    '--surface-hover': '#F3E8F8',
                    '--primary-subtle': 'rgba(123, 66, 255, 0.10)',
                    '--border-default': '#E0C8EA',
                  } as React.CSSProperties}
                >
                {/* Set Ingredients - Mobile */}
                {isSetProduct && !isViewingNested && (
                  <div>
                    <h3 className="text-base font-bold mb-1.5 text-[var(--text-primary)]">Что входит в сет</h3>
                    <p className="text-sm mb-3 text-[var(--text-secondary)]">Нажмите на товар, чтобы узнать подробности</p>
                    {isLoadingIngredients ? (
                      <div className="flex items-center justify-center py-8"><Spinner size="lg" /></div>
                    ) : setIngredients.length > 0 ? (
                      <div className="flex flex-col gap-2.5">
                        {setIngredients.map((ingredient, index) => (
                          <SetIngredientCard key={ingredient.id} ingredient={ingredient} onPress={() => handleIngredientClick(ingredient)} index={index} compact />
                        ))}
                      </div>
                    ) : (
                      <div className="py-4 text-center text-[var(--text-secondary)]">Информация о составе загружается...</div>
                    )}
                  </div>
                )}

                {/* Ingredients — отдельная карточка Mobile */}
                {p.ingredients && !isSetProduct && (
                  <div className="rounded-card bg-[var(--bg-elevated)] p-4">
                    <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-1.5">Состав</p>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                      {p.ingredients}
                    </p>
                  </div>
                )}

                {/* Аллергены — Mobile */}
                <ProductAllergens allergens={p.allergens} />

                {/* Description — отдельная карточка Mobile */}
                {p.description && (
                  <div className="rounded-card bg-[var(--bg-elevated)] p-4">
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                      {stripInfoNotices(p.description)}
                    </p>
                    <ProductInfoNotices description={p.description} />
                  </div>
                )}

                {/* Nutrition - Mobile (button + expandable) */}
                {(p.calories || p.proteins || p.fats || p.carbs) && (
                  <div>
                    <button
                      onClick={() => setShowNutrition((v) => !v)}
                      className={cn(
                        'w-full flex items-center justify-between gap-3 p-3.5 rounded-card transition-all duration-200',
                        showNutrition
                          ? 'bg-[var(--bg-elevated)] ring-1 ring-[var(--primary)]/20'
                          : 'bg-[var(--bg-elevated)]'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'flex items-center justify-center w-9 h-9 rounded-pill transition-colors',
                          showNutrition ? 'bg-[var(--primary)] text-white' : 'bg-[var(--primary-subtle)] text-[var(--primary)]'
                        )}>
                          <FireIcon weight="fill" className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-[var(--text-primary)]">Пищевая ценность</p>
                          <p className="text-xs text-[var(--text-tertiary)]">
                            {p.calories ? `${p.calories} ккал` : ''}
                            {p.weight ? ` · ${formatWeight(p.weight)}` : ''}
                          </p>
                        </div>
                      </div>
                      <CaretRight
                        weight="bold"
                        className={cn(
                          'w-4 h-4 text-[var(--text-tertiary)] transition-transform duration-200',
                          showNutrition && 'rotate-90'
                        )}
                      />
                    </button>

                    {/* Expandable content */}
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
                        showNutrition ? 'max-h-[300px] opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0'
                      )}
                    >
                      <div className="rounded-card bg-[var(--bg-elevated)] p-4 space-y-2.5">
                        {p.calories && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                              <FireIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Калории
                            </span>
                            <span className="text-sm font-semibold text-[var(--text-primary)]">{p.calories} ккал</span>
                          </div>
                        )}
                        {p.proteins && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                              <DumbbellIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Белки
                            </span>
                            <span className="text-sm font-semibold text-[var(--text-primary)]">{p.proteins}</span>
                          </div>
                        )}
                        {p.fats && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                              <DropIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Жиры
                            </span>
                            <span className="text-sm font-semibold text-[var(--text-primary)]">{p.fats}</span>
                          </div>
                        )}
                        {p.carbs && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                              <LeafIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Углеводы
                            </span>
                            <span className="text-sm font-semibold text-[var(--text-primary)]">{p.carbs}</span>
                          </div>
                        )}
                        {p.weight && (
                          <>
                            <div className="h-px bg-[var(--border-default)]" />
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-[var(--text-secondary)]">Вес</span>
                              <span className="text-sm font-semibold text-[var(--text-primary)]">{formatWeight(p.weight)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommended sauces - Mobile */}
                {sauceProducts.length > 0 && !isViewingNested && (
                  <div>
                    {/* Header in same style as nutrition button */}
                    <div className="flex items-center gap-3 p-3.5 rounded-card bg-[var(--bg-elevated)] mb-2">
                      <div className="flex items-center justify-center w-9 h-9 rounded-pill bg-[var(--primary-subtle)] text-[var(--primary)]">
                        <CartIcon weight="fill" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[var(--text-primary)]">Добавить к заказу</p>
                        <p className="text-xs text-[var(--text-tertiary)]">Соусы и дополнения</p>
                      </div>
                    </div>

                    {/* Одна колонка, весь список соусов — включая платные */}
                    <div className="flex flex-col gap-2">
                      {sauceProducts.map((sauce) => {
                        const sauceInCart = cartItems.find((item) => item.id === sauce.id);
                        const sauceQty = sauceInCart?.quantity || 0;
                        return (
                          <div
                            key={sauce.id}
                            className={cn(
                              'flex items-center gap-3 p-2.5 rounded-card',
                              'transition-all duration-200',
                              sauceQty > 0
                                ? 'bg-[var(--primary-subtle)]'
                                : 'bg-[var(--bg-elevated)]'
                            )}
                          >
                            <div className="relative w-14 h-14 rounded-card overflow-hidden bg-[var(--bg-tertiary)] flex-shrink-0">
                              <ThemedImage
                                src={sauce.image_url}
                                alt={sauce.name}
                                fill
                                className="object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-1">{sauce.name}</p>
                              <p className="text-xs font-bold text-[var(--primary)] mt-0.5">{formatPrice(sauce.price)}</p>
                            </div>
                            {sauceQty > 0 ? (
                              <QuantitySelector
                                value={sauceQty}
                                onChange={(val) => handleSauceQuantityChange(sauce.id, val)}
                                min={0}
                                max={99}
                                size="sm"
                                showRemove
                              />
                            ) : (
                              <button
                                onClick={() => handleAddSauce(sauce)}
                                className="flex items-center justify-center w-8 h-8 rounded-pill bg-[var(--primary)] text-[var(--text-on-primary)] hover:bg-[var(--primary-hover)] active:scale-95 transition-all flex-shrink-0"
                              >
                                <PlusIcon weight="bold" className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Share + App Promo — unified card */}
                {!isViewingNested && (
                  <div className="rounded-card bg-[#F3E0F8] overflow-hidden mb-8">
                    <div className="border-b border-[#E0C8EA]">
                      <ShareSection productName={p.name} productId={p.id} productPrice={p.price} compact />
                    </div>
                    <MobileAppPromo compact />
                  </div>
                )}
              </div>
              {/* end of relative z-10 content wrapper */}
              </div>
            </div>

            {/* Mobile Footer — sticky bottom with safe area */}
            <div className="sticky bottom-0 z-20 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-[var(--border-default)] bg-[var(--surface-secondary)]/95 backdrop-blur-lg">
              <div className="flex items-center gap-3">
                <QuantitySelector value={quantity} onChange={handleQuantityChange} min={1} max={99} size="lg" showRemove={false} />
                <Button onClick={handleAddToCart} disabled={!isAvailable} className="flex-1 h-13 text-base font-bold rounded-card">
                  {isAvailable ? `В корзину · ${formattedTotalPrice}` : 'Недоступно'}
                </Button>
              </div>
              {existingQuantity > 0 && (
                <p className="text-center text-xs text-[var(--text-secondary)] mt-2 flex items-center justify-center gap-1">
                  <CheckIcon weight="bold" className="w-3 h-3" />
                  Уже в корзине: {existingQuantity} шт.
                </p>
              )}
            </div>
          </div>

          {/* ============ DESKTOP LAYOUT ============ */}
          <div className="hidden md:flex md:flex-col h-full min-h-0">
            <div ref={desktopScrollRef} className="flex-1 min-h-0 overflow-y-auto overlay-scrollbar">
              {/* Image */}
              <div className="relative w-full h-[500px] flex-shrink-0 rounded-t-screen overflow-hidden">
                <ThemedImage
                  key={p.id}
                  src={p.image_url}
                  alt={p.name}
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface-secondary)] via-transparent to-transparent" />

                {/* Top left: Back / Badge */}
                <div className="absolute top-4 left-4 flex items-center gap-3">
                  {isViewingNested && (
                    <button
                      onClick={popProduct}
                      className="flex items-center justify-center w-10 h-10 rounded-pill bg-[var(--bg-overlay)] backdrop-blur-sm text-[var(--text-inverse)] hover:bg-black/50 transition-colors active:scale-95"
                    >
                      <ArrowLeft weight="bold" className="w-5 h-5" />
                    </button>
                  )}
                  <div />
                </div>
              </div>

              {/* Content */}
              <div className="max-w-4xl mx-auto px-6 -mt-20 relative">
                {/* Title row */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    {isViewingNested && (
                      <p className="text-[var(--text-tertiary)] text-sm mb-1 flex items-center gap-1">
                        <CaretRight weight="bold" className="w-3 h-3" />
                        {originalProduct?.name}
                      </p>
                    )}
                    <h1 className="text-3xl font-bold text-[var(--text-primary)] leading-tight">
                      {p.name}
                    </h1>
                  </div>

                  {/* Nutrition tooltip icon */}
                  {(p.calories || p.proteins || p.fats || p.carbs) && (
                    <div className="relative group flex-shrink-0 mt-1">
                      <button className="flex items-center justify-center w-10 h-10 rounded-pill bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--primary)] transition-colors">
                        <FireIcon weight="fill" className="w-5 h-5" />
                      </button>
                      {/* Tooltip */}
                      <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30">
                        <div className="bg-[var(--surface-primary)] border border-[var(--border-default)] rounded-card shadow-float p-4 min-w-[220px]">
                          <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">Пищевая ценность</p>
                          <div className="space-y-2">
                            {p.calories && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                  <FireIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Калории
                                </span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">{p.calories} ккал</span>
                              </div>
                            )}
                            {p.proteins && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                  <DumbbellIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Белки
                                </span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">{p.proteins}</span>
                              </div>
                            )}
                            {p.fats && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                  <DropIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Жиры
                                </span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">{p.fats}</span>
                              </div>
                            )}
                            {p.carbs && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                  <LeafIcon weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Углеводы
                                </span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">{p.carbs}</span>
                              </div>
                            )}
                          </div>
                          {p.weight && (
                            <>
                              <div className="h-px bg-[var(--border-default)] my-3" />
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-[var(--text-secondary)]">Вес</span>
                                <span className="text-sm font-semibold text-[var(--text-primary)]">{formatWeight(p.weight)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Price + Weight + Cart */}
                <div className="flex items-center gap-4 mb-6 p-4 rounded-card bg-[var(--bg-elevated)]">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="text-2xl font-bold text-[var(--primary)]">{formattedProductPrice}</span>
                      {p.weight && (
                        <span className="text-sm text-[var(--text-tertiary)]">{formatWeight(p.weight)}</span>
                      )}
                    </div>
                    {existingQuantity > 0 && (
                      <p className="text-xs text-[var(--text-tertiary)] mt-1 flex items-center gap-1">
                        <CheckIcon weight="bold" className="w-3 h-3" />
                        В корзине: {existingQuantity} шт.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <QuantitySelector value={quantity} onChange={handleQuantityChange} min={1} max={99} size="default" showRemove={false} />
                    <Button onClick={handleAddToCart} disabled={!isAvailable} className="h-11 px-6">
                      <CartIcon weight="bold" className="w-4 h-4" />
                      {isAvailable ? `${formattedTotalPrice}` : 'Недоступно'}
                    </Button>
                  </div>
                </div>

                {/* Ingredients — отдельная карточка */}
                {p.ingredients && !isSetProduct && (
                  <div className="mb-5 rounded-card bg-[var(--bg-elevated)] p-5">
                    <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-2">Состав</p>
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                      {p.ingredients}
                    </p>
                  </div>
                )}

                {/* Аллергены — Desktop */}
                <ProductAllergens allergens={p.allergens} className="mb-5" />

                {/* Description — отдельная карточка */}
                {p.description && (
                  <div className="mb-5 rounded-card bg-[var(--bg-elevated)] p-5">
                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">
                      {stripInfoNotices(p.description)}
                    </p>
                    <ProductInfoNotices description={p.description} />
                  </div>
                )}

                {/* Set Ingredients */}
                {isSetProduct && !isViewingNested && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">
                      Состав сета · {setIngredients.length}
                    </p>
                    {isLoadingIngredients ? (
                      <div className="flex items-center justify-center py-8"><Spinner size="lg" /></div>
                    ) : setIngredients.length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {setIngredients.map((ingredient, index) => (
                          <SetIngredientCard key={ingredient.id} ingredient={ingredient} onPress={() => handleIngredientClick(ingredient)} index={index} />
                        ))}
                      </div>
                    ) : (
                      <div className="py-4 text-center text-[var(--text-secondary)]">Загрузка...</div>
                    )}
                  </div>
                )}

                {/* Recommended sauces */}
                {sauceProducts.length > 0 && !isViewingNested && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-3">
                      Добавить к заказу
                    </p>
                    <div className="grid grid-cols-2 gap-2.5">
                      {sauceProducts.map((sauce) => {
                        const sauceInCart = cartItems.find((item) => item.id === sauce.id);
                        const sauceQty = sauceInCart?.quantity || 0;
                        return (
                          <div
                            key={sauce.id}
                            className={cn(
                              'flex items-center gap-3 p-2.5 rounded-card',
                              'border transition-all duration-200',
                              sauceQty > 0
                                ? 'bg-[var(--primary-subtle)] border-[var(--primary)]/20'
                                : 'bg-[var(--bg-elevated)] border-[var(--border-default)] hover:shadow-raised'
                            )}
                          >
                            <div className="relative w-16 h-16 rounded-card overflow-hidden bg-[var(--bg-tertiary)] flex-shrink-0">
                              <ThemedImage
                                src={sauce.image_url}
                                alt={sauce.name}
                                fill
                                className="object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-1 mb-1">{sauce.name}</p>
                              <div className="flex items-center justify-between gap-1">
                                <p className="text-sm font-bold text-[var(--primary)]">{formatPrice(sauce.price)}</p>
                                {sauceQty > 0 ? (
                                  <QuantitySelector
                                    value={sauceQty}
                                    onChange={(val) => handleSauceQuantityChange(sauce.id, val)}
                                    min={0}
                                    max={99}
                                    size="sm"
                                    showRemove
                                  />
                                ) : (
                                  <button
                                    onClick={() => handleAddSauce(sauce)}
                                    className="flex items-center justify-center w-8 h-8 rounded-pill bg-[var(--primary)] text-[var(--text-on-primary)] hover:bg-[var(--primary-hover)] active:scale-95 transition-all"
                                  >
                                    <PlusIcon weight="bold" className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Share + App Promo — unified card - Desktop */}
                {!isViewingNested && (
                  <div className="rounded-card overflow-hidden border border-[var(--border-default)] bg-[var(--surface)]">
                    <div className="px-4 border-b border-[var(--border-default)]">
                      <ShareSection productName={p.name} productId={p.id} productPrice={p.price} />
                    </div>
                    <MobileAppPromo />
                  </div>
                )}

                <div className="h-10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
